import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContentTab({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aboutSpeaker, setAboutSpeaker] = useState("");
  const [books, setBooks] = useState([]);

  useEffect(() => {
    let ignore = false;
    axios.get(`${API}/content`).then(({ data }) => {
      if (ignore) return;
      setAboutSpeaker(data.about_speaker || "");
      setBooks(data.books || []);
    }).catch(() => !ignore && toast.error("Could not load content.")).finally(() => !ignore && setLoading(false));
    return () => { ignore = true; };
  }, []);

  const updateBook = (i, field, value) => {
    setBooks((prev) => prev.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)));
  };

  const addBook = () => setBooks((prev) => [...prev, { title: "", url: "" }]);
  const removeBook = (i) => setBooks((prev) => prev.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    const cleaned = books.filter((b) => b.title.trim() || b.url.trim());
    if (cleaned.some((b) => !b.title.trim() || !b.url.trim())) {
      return toast.error("Each book needs both a title and a link.");
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, {
        about_speaker: aboutSpeaker,
        books: cleaned.map((b) => ({ title: b.title.trim(), url: b.url.trim() })),
      }, auth);
      setBooks(cleaned);
      toast.success("Content saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save content.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[var(--gold)] mx-auto" />;

  return (
    <div className="max-w-2xl mx-auto" data-testid="content-tab">
      <div className="ornate-frame bg-[var(--surface)] p-8">
        <h2 className="font-display text-3xl text-[var(--cream)] mb-1">Site content</h2>
        <p className="font-body text-sm text-[var(--muted)] mb-6">Edit the "About the Speaker" section and the books shown on the public page.</p>
        <form onSubmit={save} className="space-y-6">
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">About the Speaker</label>
            <textarea data-testid="content-about-input" value={aboutSpeaker} onChange={(e) => setAboutSpeaker(e.target.value)} rows={6} className="field-input resize-none" required />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="uppercase tracking-[0.2em] text-[11px] text-[var(--gold)]">Books</label>
              <button type="button" data-testid="content-add-book-btn" onClick={addBook}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] border border-[var(--gold)] text-[var(--gold)] px-3 py-1.5 hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200">
                <Plus className="w-3 h-3" /> Add book
              </button>
            </div>
            <div className="space-y-4" data-testid="content-books-list">
              {books.length === 0 && (
                <p className="font-serif-alt text-[var(--muted)]">No books yet — click "Add book".</p>
              )}
              {books.map((b, i) => (
                <div key={i} className="border border-[rgba(201,162,74,0.25)] p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <input
                      data-testid={`content-book-title-${i}`}
                      value={b.title}
                      onChange={(e) => updateBook(i, "title", e.target.value)}
                      placeholder="Book title"
                      className="field-input"
                    />
                    <button type="button" data-testid={`content-book-remove-${i}`} onClick={() => removeBook(i)} title="Remove book"
                      className="mt-3 text-[var(--muted)] hover:text-red-300 transition-colors duration-200 shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    data-testid={`content-book-url-${i}`}
                    value={b.url}
                    onChange={(e) => updateBook(i, "url", e.target.value)}
                    placeholder="https://..."
                    className="field-input"
                  />
                </div>
              ))}
            </div>
          </div>

          <button type="submit" data-testid="content-save-btn" disabled={saving}
            className="w-full rounded-full bg-[var(--gold)] text-[var(--bg)] py-4 uppercase tracking-[0.2em] text-xs font-semibold flex items-center justify-center gap-2 hover:bg-[var(--cream)] transition-colors duration-300 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save content
          </button>
        </form>
      </div>
    </div>
  );
}
